import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Button, Card, CardBody, CardHeader } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { getFuelSupplySummaryByEstablishment } from '../../../../../slices/fuelSupply/thunk';
import getChartColorsArray from '../../../../../Components/Common/ChartsDynamicColor';
import { getProductiveCycleActive } from '../../../../../slices/thunks';

import moment from 'moment';
import 'moment/locale/es'; // Importar el idioma español
moment.locale('es'); // Configurar el idioma

const Fuel = ({ establishment_id, dataColors }) => {
    const chartTimelineBasicColors = getChartColorsArray(dataColors);

    const dispatch = useDispatch();
    const [selectedView, setSelectedView] = useState("all");
    const [series, setSeries] = useState([]);
    const [categories, setCategories] = useState([]);
    const [colors, setColors] = useState([]);

    // const selectorEstablishments = createSelector(
    //     state => state.Establishments,
    //     establishment => ({
    //         productive_cycle: establishment.productiveCycleActiveDetail,
    //         isLoading: establishment.isLoading,
    //         isSuccess: establishment.isSuccess,
    //         isError: establishment.isError,
    //     })
    // );

    // const { productive_cycle } = useSelector(selectorEstablishments);

    // useEffect(() => {
    //     dispatch(getProductiveCycleActive(establishment_id));
    // }, [establishment_id]);

    const selectorSupply = createSelector(
        state => state.FuelSupply,
        result => ({
            fuelData: result.itemFuelSupplyList,
            isLoading: result.isLoadingList,
        })
    );

    const { fuelData } = useSelector(selectorSupply);

    useEffect(() => {
        if (establishment_id) {
            dispatch(getFuelSupplySummaryByEstablishment({ establishment_id: establishment_id, code: selectedView, }));
        }
    }, [establishment_id, selectedView]);

    useEffect(() => {
        if (fuelData?.length) {
            if (selectedView === "all") {
                // Agrupar por fuel_tank_name
                const groupedData = fuelData.reduce((acc, item) => {
                    const key = `${item.fuel_tank_name} (${item.unit_measurement_name})`; // Incluye la unidad
                    if (!acc[key]) {
                        acc[key] = { IN: 0, OUT: 0 };
                    }
                    acc[key][item.fuel_supply_type_direction] += item.total_amount;
                    return acc;
                }, {});

                // Series para IN y OUT
                const seriesChart = [
                    {
                        name: 'Entradas',
                        data: Object.values(groupedData).map(item => item.IN || 0),
                    },
                    {
                        name: 'Salidas',
                        data: Object.values(groupedData).map(item => item.OUT || 0),
                    },
                ];

                const labelsChart = Object.keys(groupedData);

                setSeries(seriesChart);
                setCategories(labelsChart);
                setColors([chartTimelineBasicColors[0], chartTimelineBasicColors[1]]); // Colores en orden
            } else if (selectedView === "type") {
                // Agrupar por fuel_tank_name y fuel_supply_type_name
                const groupedData = fuelData.reduce((acc, item) => {
                    const tankKey = `${item.fuel_tank_name} (${item.unit_measurement_name})`; // Incluye la unidad
                    const typeKey = item.fuel_supply_type_name;
                    if (!acc[tankKey]) {
                        acc[tankKey] = {};
                    }
                    if (!acc[tankKey][typeKey]) {
                        acc[tankKey][typeKey] = { IN: 0, OUT: 0 };
                    }
                    acc[tankKey][typeKey][item.fuel_supply_type_direction] += item.total_amount;
                    return acc;
                }, {});

                const consolidatedData = {};
                const labelsChart = Object.keys(groupedData);

                labelsChart.forEach(tankKey => {
                    Object.keys(groupedData[tankKey]).forEach(typeKey => {
                        if (!consolidatedData[typeKey]) {
                            consolidatedData[typeKey] = { IN: [], OUT: [] };
                        }
                        consolidatedData[typeKey].IN.push(groupedData[tankKey][typeKey]?.IN || 0);
                        consolidatedData[typeKey].OUT.push(groupedData[tankKey][typeKey]?.OUT || 0);
                    });
                });

                // Crear las series y separarlas por IN primero, luego OUT
                const seriesIN = [];
                const seriesOUT = [];

                Object.keys(consolidatedData).forEach(typeKey => {
                    if (consolidatedData[typeKey].IN.some(value => value > 0)) {
                        seriesIN.push({
                            name: `${typeKey}`,
                            data: consolidatedData[typeKey].IN,
                        });
                    }
                    if (consolidatedData[typeKey].OUT.some(value => value > 0)) {
                        seriesOUT.push({
                            name: `${typeKey}`,
                            data: consolidatedData[typeKey].OUT,
                        });
                    }
                });

                const orderedSeriesChart = [...seriesIN, ...seriesOUT]; // IN primero, luego OUT

                const colorsArray = orderedSeriesChart.map((serie, index) =>
                    index < seriesIN.length
                        ? chartTimelineBasicColors[0] // IN
                        : chartTimelineBasicColors[1] // OUT
                );

                setSeries(orderedSeriesChart);
                setCategories(labelsChart);
                setColors(colorsArray); // Actualiza los colores
            } else if (selectedView === "month") {
                // Agrupar por mes y tanque
                const groupedData = fuelData.reduce((acc, item) => {
                    const monthKey = item.month; // Agrupar por mes (en formato 'YYYY-MM')
                    const tankKey = `${item.fuel_tank_name} (${item.unit_measurement_name})`; // Identificar el tanque
    
                    if (!acc[monthKey]) {
                        acc[monthKey] = {};
                    }
                    if (!acc[monthKey][tankKey]) {
                        acc[monthKey][tankKey] = { IN: 0, OUT: 0 }; // Inicializar saldos
                    }
    
                    // Acumular valores por dirección
                    acc[monthKey][tankKey][item.fuel_supply_type_direction] += item.total_amount;
    
                    return acc;
                }, {});
    
                // Obtener las etiquetas (labelsChart) por mes en formato MMM-YY en español
                const rawLabels = Object.keys(groupedData).sort(); // Meses crudos en 'YYYY-MM'
                const labelsChart = rawLabels.map(monthKey =>
                    moment(monthKey, 'YYYY-MM').format('MMM-YY').replace('.', '') // Eliminar el punto final
                ); 

                // Crear las series por tanque con saldos (IN - OUT)
                const seriesData = {};
                rawLabels.forEach((rawMonthKey, rawIndex) => {
                    Object.entries(groupedData[rawMonthKey]).forEach(([tankKey, values]) => {
                        if (!seriesData[tankKey]) {
                            seriesData[tankKey] = Array(labelsChart.length).fill(0);
                        }
                        // Calcular saldo y redondear a enteros
                        seriesData[tankKey][rawIndex] = Math.round(values.IN - values.OUT);
                    });
                });
    
                // Formatear las series finales
                const formattedSeries = Object.entries(seriesData).map(([tankKey, data]) => ({
                    name: tankKey,
                    data,
                }));
    
                // Generar colores para cada serie
                const colorsArray = [chartTimelineBasicColors[0], chartTimelineBasicColors[2]];
    
                // Actualizar los estados
                setSeries(formattedSeries);
                setCategories(labelsChart);
                setColors(colorsArray); // Actualiza los colores
            }


        }
    }, [fuelData, selectedView]);




    const options = {
        chart: {
            type: selectedView === "month" ? "line" : "bar",
            height: 200,
            toolbar: {
                show: false, // Ocultar la barra de herramientas
            },
        },
        plotOptions: {
            bar: {
                horizontal: false,
                dataLabels: {
                    position: 'top',
                },
            },
        },
        stroke: {
            width: selectedView === "month" ? 2 : 0, // Grosor de línea: 2 para "line", 0 para "bar"
        },
        xaxis: {
            categories: categories,
            labels: selectedView === "month" ? { // Solo aplicar configuración si es "line"
                rotate: -70,
                hideOverlappingLabels: true,
                style: {
                    fontSize: '8px', // Ajusta el tamaño de la letra
                },
            } : {}, // Configuración vacía si no es "line"
        },
        yaxis: {
            title: {
                text: "Movimientos",
            },
        },
        colors: colors,
        legend: {
            position: 'bottom',
            horizontalAlign: 'center',
        },
        tooltip: {
            y: {
                formatter: function (val) {
                    // Condicionar para mostrar 'Saldo' solo en gráficos de tipo línea
                    return selectedView === "month" ? `Saldo: ${val}` : val;
                },
            },
        },
    };
    

    if (!fuelData || fuelData.length === 0) {
        return (
            <Card style={{ height: "100%" }}>
                <CardHeader className='p-2 align-items-center d-flex'>
                    <h5 className="card-title mb-0 flex-grow-1">Movimientos de Combustible</h5>
                </CardHeader>
                <CardBody className='p-2'>
                    <div className="d-flex justify-content-center align-items-center p-3">
                        <div className="text-center">
                            <div className="avatar-md">
                                <span className="avatar-title bg-info-subtle rounded-circle fs-1">
                                    <i className={`${Icons.fuel} text-info`}></i>
                                </span>
                            </div>
                        </div>
                    </div>
                    <p className="fs-11 lh-base text-center">No hay movimientos de Combustible</p>
                </CardBody>
            </Card>
        );
    }

    return (
        <Card style={{ height: "100%" }}>
            <CardHeader className="p-2 align-items-center d-flex">
                <h5 className="card-title mb-0 flex-grow-1">Movimientos de Combustible</h5>
                <div className="btn-box-dashboard">
                    <Button
                        color="primary"
                        size="sm"
                        className="btn-soft-secondary me-1"
                        onClick={() => setSelectedView('all')}
                        active={selectedView === 'all'}
                    >
                        Total
                    </Button>
                    <Button
                        color="secondary"
                        size="sm"
                        className="btn-soft-secondary me-1"
                        onClick={() => setSelectedView('type')}
                        active={selectedView === 'type'}
                    >
                        Tipo
                    </Button>
                    <Button
                        color="secondary"
                        size="sm"
                        className="btn-soft-secondary me-1"
                        onClick={() => setSelectedView('month')}
                        active={selectedView === 'month'}
                    >
                        Mes
                    </Button>
                </div>
            </CardHeader>
            <CardBody className="p-2">
                <div className="w-100">
                    <div dir="ltr">
                        <ReactApexChart
                            key={selectedView} // Agregar key basado en selectedView
                            options={{
                                ...options,
                                noData: {
                                    text: "No hay datos disponibles",
                                    align: "center",
                                    verticalAlign: "middle",
                                    style: {
                                        color: "#999",
                                        fontSize: "11px",
                                    },
                                },
                            }}
                            series={series}
                            type={selectedView === "month" ? "line" : "bar"} // Tipo dinámico
                            height={200}
                        />

                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default Fuel;
